import {
  buildContent,
  type ContentSegment,
  type ToolUseGroupSegment,
  type ToolUseNode,
  type ChatMessage,
  type StreamingEvent,
} from '@circuit/chat-utils';
import { preprocessContent } from '@circuit/markdown/utils';
import React, { useMemo, useState, useCallback } from 'preact/compat';

import { ChatMarkdown } from './ChatMarkdown';
import CopyButton from './CopyButton';

interface AssistantMessageProps {
  events: StreamingEvent[];
  isStreaming: boolean;
}

const SKIP_EVENTS = new Set([
  'progress',
  'ref_start',
  'ref',
  'ref_group',
  'error',
]);

/**
 * Strips directive markup the widget's simple markdown renderer can't handle.
 * The main app uses EnhancedMarkdown for these; the widget just shows plain text.
 */
const DIRECTIVE_RE =
  / ?:(?:ref-start|ref-end|ref-group|legacy-ref)\{[^}]*\} ?/g;
const BLOCK_DIRECTIVE_RE = /^:::(?:\w+)?\s*$/gm;

function stripDirectives(text: string): string {
  return text.replace(DIRECTIVE_RE, '').replace(BLOCK_DIRECTIVE_RE, '').trim();
}

/**
 * Builds the plain-text representation of an assistant message for copying.
 * Includes rendered text and image alt/URLs; tool-use chrome is omitted.
 */
function getCopyableText(content: ContentSegment[]): string {
  const parts: string[] = [];
  for (const segment of content) {
    if (segment.type === 'text') {
      // Mirror what ChatMarkdown renders: strip directives, then run the
      // same preprocessing (bullet + reference path normalization) so the
      // copied text matches what the user sees on screen.
      const cleaned = preprocessContent(stripDirectives(segment.content));
      if (cleaned) parts.push(cleaned);
    } else if (segment.type === 'image') {
      if (segment.src) parts.push(segment.src);
    }
  }
  return parts.join('\n\n').trim();
}

function toChatMessage(events: StreamingEvent[]): ChatMessage {
  return {
    id: '',
    chatId: '',
    events,
    role: 'assistant',
  };
}

/**
 * Extracts the latest progress message from the raw event stream.
 * Returns null if text has arrived after the last progress (progress is superseded).
 */
function getLatestProgressMessage(events: StreamingEvent[]): string | null {
  let latestProgress: string | null = null;
  for (const event of events) {
    if (event.event === 'progress') {
      latestProgress =
        typeof event.data === 'string'
          ? event.data
          : event.data
            ? String(event.data)
            : '';
    } else if (event.event === 'text') {
      latestProgress = null;
    }
  }
  return latestProgress;
}

/**
 * Reads the latest message for a tool-use node.
 * Completed tools have data.result; in-progress tools store progress in children.
 */
function getToolResultMessage(toolUse: ToolUseNode): string {
  if (toolUse.data.result) return toolUse.data.result;
  for (let i = toolUse.children.length - 1; i >= 0; i--) {
    const child = toolUse.children[i];
    if (child.type === 'text') {
      const text = child.content.trim();
      if (text) return text;
    }
  }
  return '';
}

const SparkleIcon: React.FC = () => (
  <svg
    className='assistant-sparkle-icon'
    width='11'
    height='22'
    viewBox='0 0 11 22'
    fill='none'
    aria-hidden='true'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M5.49943 8.71178L3.94108 21.5641C3.878 22.0226 4.52052 22.1937 4.77285 21.709L10.9339 9.34855C11.1091 9.0753 10.9152 8.71178 10.5974 8.71178H5.50177H5.49943ZM0.40382 8.71178C0.139808 8.71178 -0.0541509 8.45512 0.0136043 8.19375L0.9809 0.305112C1.06034 -0.00140497 1.43647 -0.103582 1.65609 0.122147L5.89895 4.09498C5.99941 4.19952 6.03917 4.34926 6.00179 4.49182L5.49943 8.71416H0.40382V8.71178Z'
      fill='currentColor'
    />
  </svg>
);

const ToolIcon: React.FC<{ status: 'in-progress' | 'completed' | 'error' }> = ({
  status,
}) => {
  if (status === 'in-progress') {
    return (
      <span
        className='assistant-tool-spinner'
        aria-hidden='true'
      />
    );
  }
  if (status === 'error') {
    return (
      <span
        className='assistant-tool-error-icon'
        aria-hidden='true'
      >
        ✗
      </span>
    );
  }
  return (
    <span
      className='assistant-tool-check'
      aria-hidden='true'
    >
      ✓
    </span>
  );
};

interface ToolLineProps {
  segment: ToolUseGroupSegment;
}

const ToolLine: React.FC<ToolLineProps> = ({ segment }) => {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((v) => !v), []);

  const { toolUses } = segment;

  const hasError = toolUses.some((t) => t.data.is_error);
  const allCompleted = toolUses.every(
    (t) => t.data.status === 'completed' && !t.data.is_error,
  );
  const activeToolUse =
    !allCompleted && !hasError
      ? [...toolUses].reverse().find((t) => t.data.status === 'pending')
      : undefined;

  let statusClass: string;
  let label: string;
  let iconStatus: 'in-progress' | 'completed' | 'error';

  if (hasError) {
    const errorTool = toolUses.find((t) => t.data.is_error);
    statusClass = 'error';
    iconStatus = 'error';
    label = `${errorTool?.data.name || 'Tool'} — ${errorTool?.data.result || 'Failed'}`;
  } else if (activeToolUse) {
    const message = getToolResultMessage(activeToolUse);
    statusClass = 'in-progress';
    iconStatus = 'in-progress';
    label = activeToolUse.data.name + (message ? ` — ${message}` : '');
  } else if (allCompleted) {
    statusClass = 'completed';
    iconStatus = 'completed';
    label =
      toolUses.length === 1
        ? toolUses[0].data.name || 'Tool'
        : `Ran ${toolUses.length} tools`;
  } else {
    return null;
  }

  const canExpand = allCompleted && toolUses.length > 0;

  return (
    <div className={`assistant-tool-group ${expanded ? 'expanded' : ''}`}>
      <button
        type='button'
        className={`assistant-tool-line ${statusClass}`}
        onClick={canExpand ? toggle : undefined}
        aria-expanded={canExpand ? expanded : undefined}
      >
        <ToolIcon status={iconStatus} />
        <span>{label}</span>
        {canExpand && (
          <span
            className='assistant-tool-chevron'
            aria-hidden='true'
          >
            {expanded ? '▾' : '▸'}
          </span>
        )}
      </button>
      {expanded && (
        <div className='assistant-tool-details'>
          {toolUses.map((toolUse) => {
            const msg = getToolResultMessage(toolUse);
            const isError = toolUse.data.is_error;
            return (
              <div
                key={toolUse.id}
                className={`assistant-tool-detail-item ${isError ? 'error' : ''}`}
              >
                <ToolIcon status={isError ? 'error' : 'completed'} />
                <span className='assistant-tool-detail-name'>
                  {toolUse.data.name}
                </span>
                {msg && (
                  <span className='assistant-tool-detail-msg'>{msg}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const renderSegments = (
  content: ContentSegment[],
  isStreaming: boolean,
  progressMessage: string | null,
) => {
  const nodes: React.ReactNode[] = [];
  let bubbleItems: React.ReactNode[] = [];

  const flushBubble = () => {
    if (bubbleItems.length === 0) return;
    nodes.push(
      <div
        className='message bot'
        key={`bubble-${nodes.length}`}
      >
        <div className='assistant-message-content'>{bubbleItems}</div>
      </div>,
    );
    bubbleItems = [];
  };

  content.forEach((segment, index) => {
    switch (segment.type) {
      case 'text': {
        const cleaned = stripDirectives(segment.content);
        if (!cleaned) break;
        bubbleItems.push(
          <ChatMarkdown
            key={`text-${index}`}
            isStreaming={isStreaming && segment.isLastText}
          >
            {cleaned}
          </ChatMarkdown>,
        );
        break;
      }
      case 'image': {
        bubbleItems.push(
          <figure
            className='assistant-image'
            key={`image-${index}`}
          >
            <img
              src={segment.src}
              alt={segment.alt}
            />
          </figure>,
        );
        break;
      }
      case 'tool-use-group': {
        flushBubble();
        nodes.push(
          <ToolLine
            key={segment.groupKey}
            segment={segment}
          />,
        );
        break;
      }
    }
  });

  flushBubble();

  if (progressMessage !== null) {
    nodes.push(
      <div
        className='assistant-progress'
        key='progress'
      >
        <SparkleIcon />
        <span>{progressMessage}</span>
      </div>,
    );
  }

  return nodes;
};

export const AssistantMessage: React.FC<AssistantMessageProps> = ({
  events,
  isStreaming,
}) => {
  const progressMessage = useMemo(
    () => getLatestProgressMessage(events),
    [events],
  );

  const contentEvents = useMemo(
    () => events.filter((e) => !SKIP_EVENTS.has(e.event)),
    [events],
  );

  const errorMessages = useMemo(
    () =>
      events
        .filter((e): e is StreamingEvent<'error'> => e.event === 'error')
        .map((e) => e.data.message ?? 'An error occurred'),
    [events],
  );

  const { content } = useMemo(
    () =>
      contentEvents.length > 0
        ? buildContent(toChatMessage(contentEvents), {
            isStreaming,
          })
        : { content: [], chunks: {} },
    [contentEvents, isStreaming],
  );

  const elements = useMemo(
    () => renderSegments(content, isStreaming, progressMessage),
    [content, isStreaming, progressMessage],
  );

  const copyText = useMemo(() => getCopyableText(content), [content]);

  return (
    <>
      {elements}
      {!isStreaming && copyText && (
        <div className='assistant-message-actions'>
          <CopyButton text={copyText} />
        </div>
      )}
      {errorMessages.map((msg, i) => (
        <div
          key={`error-${i}`}
          className='message bot'
        >
          <div className='assistant-error-message'>{msg}</div>
        </div>
      ))}
    </>
  );
};
